import { useState, useEffect, useRef } from 'react';

export function useDualViewSizing(showDualView: boolean, leftDrawerOpen: boolean, rightDrawerOpen: boolean) {
  const referenceContainerRef = useRef<HTMLDivElement>(null);
  const workspaceContainerRef = useRef<HTMLDivElement>(null);
  const [referenceSize, setReferenceSize] = useState<{ w: number, h: number } | null>(null);
  const [workspaceSize, setWorkspaceSize] = useState<{ w: number, h: number } | null>(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (entry.target === referenceContainerRef.current) {
          setReferenceSize({ w: width, h: height });
        } else if (entry.target === workspaceContainerRef.current) {
          setWorkspaceSize({ w: width, h: height });
        }
      }
    });

    if (referenceContainerRef.current) observer.observe(referenceContainerRef.current);
    if (workspaceContainerRef.current) observer.observe(workspaceContainerRef.current);

    return () => observer.disconnect();
  }, [showDualView, leftDrawerOpen, rightDrawerOpen]);

  const getUnifiedSquareStyle = () => {
    if (!referenceSize || !workspaceSize) {
      return {
        reference: { width: '100%' as string | number, height: '100%' as string | number },
        workspace: { width: '100%' as string | number, height: '100%' as string | number }
      };
    }

    const commonHeight = Math.min(referenceSize.h, workspaceSize.h);
    const refSide = Math.min(referenceSize.w, commonHeight);
    const workSide = Math.min(workspaceSize.w, commonHeight);
    const finalSide = Math.min(refSide, workSide);

    return {
      reference: { width: finalSide as string | number, height: finalSide as string | number },
      workspace: { width: finalSide as string | number, height: finalSide as string | number }
    };
  };

  const unifiedSizes = getUnifiedSquareStyle();

  return {
    referenceContainerRef,
    workspaceContainerRef,
    unifiedSizes,
  };
}
